import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';

import { TranslocoDirective } from '@jsverse/transloco';

import { LanguageService } from '../../../core/i18n/language.service';
import { MeasurePipe } from '../../../core/units/measure.pipe';
import { Icon } from '../icon/icon';
import { TopbarPopover } from '../popover/topbar-popover';
import { PopoverPanelDirective } from '../popover/popover-panel.directive';
import { weatherIconName } from './weather-icon';
import { WeatherService } from './weather.service';

const COMPASS_KEYS = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'] as const;

/**
 * Weather — the topbar chip (condition glyph + temperature) that toggles a
 * popover with the full current conditions from OpenWeather. The chip sits on
 * the dark header and paints `currentColor` like the rest of the status
 * toolbar; the popover is a regular card via semantic tokens, so it themes
 * for free. Overlay behaviour lives in the shared `app-topbar-popover`.
 */
@Component({
  selector: 'app-weather',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon, MeasurePipe, TranslocoDirective, TopbarPopover, PopoverPanelDirective],
  templateUrl: './weather.html',
  host: { class: 'block' },
})
export class Weather {
  private readonly languageService = inject(LanguageService);
  protected readonly weather = inject(WeatherService);

  // Minute tick so "updated N min ago" stays honest while the popover is open.
  private readonly nowMs = signal(Date.now());

  constructor() {
    const id = setInterval(() => this.nowMs.set(Date.now()), 60_000);
    inject(DestroyRef).onDestroy(() => clearInterval(id));
  }

  protected readonly icon = computed(() => {
    const code = this.weather.current()?.iconCode;
    return code ? weatherIconName(code) : ('icon-cloud' as const);
  });

  protected readonly minutesAgo = computed(() => {
    const at = this.weather.updatedAt();
    return at == null ? null : Math.max(0, Math.floor((this.nowMs() - at) / 60_000));
  });

  /** Wind bearing bucketed to an 8-point compass i18n key. */
  protected readonly compassKey = computed(() => {
    const deg = this.weather.current()?.windDeg;
    return deg == null ? null : COMPASS_KEYS[Math.round(deg / 45) % 8];
  });

  protected readonly sunrise = computed(() => this.formatTime(this.weather.current()?.sunriseMs));
  protected readonly sunset = computed(() => this.formatTime(this.weather.current()?.sunsetMs));

  private formatTime(epochMs: number | undefined): string {
    if (epochMs == null) {
      return '';
    }
    return new Date(epochMs).toLocaleTimeString(this.languageService.current(), {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
